import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import { isBookingProcessAlias } from '../../transactions/transaction';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: React nodes for the price block content (styled amount + per-unit)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + plain price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  // Visible price block uses JSX spans for styling; aria-label needs a plain string.
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const priceMessagePlain =
    showPrice && formattedPrice != null
      ? intl.formatMessage(
          { id: priceMessageId },
          { priceValue: formattedPrice, pricePerUnit: perUnitString }
        )
      : '';

  // Size information (handles single size enum/text or multiple sizes multi-enum/array)
  const listingFields = config?.listing?.listingFields || [];
  const sizeFieldConfig = listingFields.find(
    f =>
      f?.key?.toLowerCase() === 'size' ||
      f?.key?.toLowerCase() === 'sizes' ||
      f?.key?.toLowerCase()?.includes('size') ||
      f?.showConfig?.label?.toLowerCase() === 'size' ||
      f?.showConfig?.label?.toLowerCase()?.includes('size')
  );

  const publicDataKeys = Object.keys(publicData || {});
  const matchingKey =
    sizeFieldConfig?.key ||
    publicDataKeys.find(
      k =>
        k.toLowerCase() === 'size' ||
        k.toLowerCase() === 'sizes' ||
        k.toLowerCase() === 'clothingsize' ||
        k.toLowerCase() === 'clothing_size'
    );

  const rawSize =
    (matchingKey ? publicData[matchingKey] : null) ??
    publicData?.Size ??
    publicData?.size ??
    publicData?.sizes ??
    publicData?.clothingSize ??
    publicData?.clothing_size;

  let sizeMessage = null;
  if (rawSize != null && rawSize !== '') {
    const enumOptions = sizeFieldConfig?.enumOptions || [];
    const getOptionLabel = val => {
      const opt = enumOptions.find(o => `${o.option}` === `${val}`);
      return opt?.label || `${val}`;
    };

    if (Array.isArray(rawSize)) {
      const validSizes = rawSize.filter(s => s != null && s !== '');
      if (validSizes.length > 0) {
        const labels = validSizes.map(getOptionLabel);
        const isMultiple = labels.length > 1;
        const messageId = isMultiple ? 'ListingCard.sizes' : 'ListingCard.size';
        const formattedValues = labels.join(', ');
        sizeMessage = intl.formatMessage(
          { id: messageId },
          isMultiple ? { sizes: formattedValues } : { size: formattedValues }
        );
      }
    } else {
      const stringSize = `${rawSize}`.trim();
      const isMultiple = stringSize.includes(',');
      const label = getOptionLabel(rawSize);
      const messageId = isMultiple ? 'ListingCard.sizes' : 'ListingCard.size';
      sizeMessage = intl.formatMessage(
        { id: messageId },
        isMultiple ? { sizes: label } : { size: label }
      );
    }
  }

  const cardAriaLabel =
    priceMessagePlain.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessagePlain }
        )
      : title;

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    sizeMessage,
  };
};
